import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { del, get, patch } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type { TagSummary } from "../lib/types";
import { Shell } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Empty, Failed, Panel } from "../components/ui";

export function Tags() {
  const client = useQueryClient();
  const can = usePermission();
  const canManage = can("tags:manage");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState("");

  const listing = useQuery({ queryKey: ["tags"], queryFn: () => get<{ tags: TagSummary[] }>("/v1/tags") });

  const rename = useMutation({
    mutationFn: (id: string) => patch<TagSummary>(`/v1/tags/${id}`, { name }),
    onSuccess: () => {
      setRenaming(null);
      void client.invalidateQueries({ queryKey: ["tags"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/v1/tags/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["tags"] }),
  });

  const rows = listing.data?.tags ?? [];

  return (
    <Shell fill crumb={<span className="text-text">tags</span>}>
      <Panel fill title="tags">
        {listing.isError ? (
          <Failed error={listing.error} what="tags" />
        ) : !listing.isPending && rows.length === 0 ? (
          <Empty
            title="no tags yet"
            hint="A tag is born by being typed into a document's tag field — there is no separate way to create one."
          />
        ) : (
          <DataTable
            fill
            table="tags"
            loading={listing.isPending}
            rows={rows}
            rowKey={(row) => row.id}
            columns={[
              {
                key: "name",
                header: "name",
                width: 240,
                render: (row) =>
                  renaming === row.id ? (
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (name.trim() !== "") rename.mutate(row.id);
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        aria-label={`rename ${row.name}`}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        autoFocus
                        className="min-w-0 flex-1 border border-hair bg-transparent px-1.5 py-0.5 font-mono text-[12px]"
                      />
                    </form>
                  ) : (
                    <Link
                      to={`/documents?tag=${encodeURIComponent(row.name)}`}
                      className="hover:text-gold"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.name}
                    </Link>
                  ),
              },
              {
                key: "color",
                header: "",
                width: 40,
                render: (row) =>
                  row.color ? (
                    <i className="inline-block h-3 w-3" style={{ background: row.color }} />
                  ) : null,
              },
              {
                key: "count",
                header: "documents",
                width: 110,
                align: "end",
                cellClass: "tnum font-mono",
                render: (row) => row.document_count,
              },
              {
                key: "actions",
                header: "",
                width: 160,
                render: (row) =>
                  canManage ? (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setName(row.name);
                          setRenaming(row.id);
                        }}
                      >
                        rename
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(row.id)}
                        className="hover:!text-crit"
                      >
                        delete
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
